/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-useless-escape */
import Slider from '@mui/material/Slider';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

// tslint:disable-next-line
import Grid from '@mui/material/Grid';
import Repeat from '@mui/icons-material/Repeat';
import cx from 'classnames';
import * as React from 'react';

import AudioDownloadsControl from './AudioDownloadsControl';
import AudioPlayControl from './AudioPlayControl';
import AudioVolumeControl from './AudioVolumeControl';
import AudioPlayerCloseButton from './AudioPlayerCloseButton';
import { actionCreators } from './state/actions';
import { getFormattedTime, populateDispatch } from './state/helpers';
import PLAYER from './state/player';
import reducer from './state/reducer';
import {
    IAudioPlayerClassNameProps,
    IAudioPlayerColors,
    Icons,
    AudioPlayerComponentsOrder,
    AudioPlayerVariation,
    TimeOption,
    TimePosition,
    getColors,
} from '../types';

// Re-export types for backwards compatibility
export type { IAudioPlayerClassNameProps, IAudioPlayerColors, Icons } from '../types';
export {
    AudioPlayerComponentsOrder,
    AudioPlayerVariation,
    TimeOption,
    TimePosition,
    getColors,
} from '../types';

const isSafari = !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/);

const initialState = {
    player: {
        duration: 0,
        remaining: 0,
        progress: 0,
        current: 0,
        loop: false,
        autoplay: false,
    },
};

enum AudioPlayerPreload {
    auto = 'auto',
    metadata = 'metadata',
    none = 'none',
}

interface IAudioPlayerProps {
    src: string | string[];
    rounded?: boolean;
    elevation?: number;
    useStyles?: any;
    width?: string;
    height?: string;
    download?: boolean;
    volume?: boolean;
    muted?: boolean | null;
    variation?: keyof typeof AudioPlayerVariation;
    preload?: AudioPlayerPreload;
    loop?: boolean;
    order?: keyof typeof AudioPlayerComponentsOrder;
    displaySlider?: boolean;
    displayCloseButton?: boolean;
    // some browsers will block audio autoplay
    autoplay?: boolean;
    debug?: boolean;
    spacing?: number;
    icons?: Icons;
    time?: keyof typeof TimeOption;
    timePosition?: keyof typeof TimePosition;
    onPlayed?: (event: any) => void;
    onPaused?: (event: any) => void;
    onFinished?: (event: any) => void;
    getPlayer?: (player: HTMLAudioElement | null, dispatch: React.Dispatch<any>) => void;
    onClose?: () => void;
}

const AudioPlayer: React.FunctionComponent<IAudioPlayerProps> = ({
    src,
    rounded = false,
    elevation = 1,
    useStyles = () => ({}),
    width = '100%',
    height = 'auto',
    variation = AudioPlayerVariation.default,
    preload = AudioPlayerPreload.auto,
    volume = false,
    muted = null,
    download = false,
    autoplay = false,
    order = AudioPlayerComponentsOrder.standard,
    loop = false,
    debug = false,
    spacing = undefined,
    time = 'double',
    timePosition = 'start',
    displaySlider = false,
    displayCloseButton = false,
    icons,
    onPlayed = (event: any) => {},
    onPaused = (event: any) => {},
    onFinished = (event: any) => {},
    getPlayer = (player: HTMLAudioElement | null, dispatch: React.Dispatch<any>) => {},
    onClose = () => {},
}) => {
    const player = React.useRef<HTMLAudioElement | null>(null);
    const [visible, setVisibility] = React.useState(true);
    const handleClose = React.useCallback(() => {
        setVisibility(false);
        onClose();
    }, []);
    const theme = useTheme();
    const playerColors: IAudioPlayerColors = getColors(theme, variation);
    const componentsOrder = order === AudioPlayerComponentsOrder.standard ? 'unset' : '-1';
    const componentStyles = { width, height, playerColors, componentsOrder };
    // const classes = useComponentStyles(componentStyles);
    const classNames: Partial<IAudioPlayerClassNameProps> = useStyles(componentStyles);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSingleTime = React.useMemo(() => time === TimeOption.single, [time]);
    const isTimePositionStart = React.useMemo(
        () => timePosition === TimePosition.start,
        [timePosition],
    );
    const [showCurrentTime, toggleTime] = React.useState(true);
    const toggleCurrentTime = React.useCallback(() => toggleTime((val) => !val), [toggleTime]);

    const [state, dispatch] = React.useReducer(reducer, initialState);
    const [
        _playAudio,
        _pauseAudio,
        _muteAudio,
        _unmuteAudio,
        _changeAudioVolume,
        _setPlayerDuration,
        _setPlayerTime,
        _audioEnded,
        _replayAudio,
        _changePlayerSlider,
        _setPlayerAutoplay,
        _loopAudio,
    ] = React.useMemo(() => {
        // `populateDispatch` forwards the ref object, never reads `.current` during render.
        // eslint-disable-next-line react-hooks/refs
        return populateDispatch(dispatch, player, ...actionCreators);
    }, [dispatch, player, actionCreators]);
    const handleAudioSliderChange = (event: object, progress: any) => {
        _changePlayerSlider(progress);
    };
    const handlePlayerTimeUpdate = () => {
        _setPlayerTime();
    };
    // @ts-ignore
    const handleAudioEnd = (event) => {
        _audioEnded();
        onFinished(event);
    };
    const onLoad = () => {
        if (player?.current?.duration === Infinity) {
            player.current.currentTime = 24 * 60 * 60;
            player.current.currentTime = 0;
        }
        _setPlayerDuration();
        getPlayer(player.current, dispatch);
        if (player?.current?.currentTime === 0) {
            if (player?.current?.autoplay || player?.current?.loop) {
                // @ts-ignore: no-empty
            } else {
                _pauseAudio();
            }
        }
    };

    React.useEffect(() => {
        if (player && player.current) {
            if (player.current.readyState > 3) {
                onLoad();
            }
            if (!player.current.autoplay && autoplay) {
                _setPlayerAutoplay();
            }
            if (isSafari) {
                player.current.load();
            }
            player.current.addEventListener('canplay', onLoad);
            player.current.addEventListener('timeupdate', handlePlayerTimeUpdate);
            player.current.addEventListener('ended', handleAudioEnd);
            player.current.addEventListener('pause', onPaused);
            player.current.addEventListener('play', onPlayed);
        }
        return () => {
            if (player && player.current) {
                player.current.removeEventListener('canplay', onLoad);

                player.current.removeEventListener('timeupdate', handlePlayerTimeUpdate);
                player.current.removeEventListener('ended', handleAudioEnd);
                player.current.removeEventListener('pause', onPaused);
                player.current.removeEventListener('play', onPlayed);
            }
        };
    }, [player, src]);

    React.useEffect(() => {
        if (player?.current && typeof muted === 'boolean') {
            if (muted) {
                _muteAudio();
            } else {
                _unmuteAudio();
            }
        }
    }, [muted]);

    // In an effect, not render — reading `player.current` while rendering is not allowed.
    React.useEffect(() => {
        if (!debug) return;
        console.log('state', state);
        console.log('player', player.current);
    }, [debug, state]);

    const handleLoop = () => {
        _loopAudio(!state.player.loop);
    };
    const mainContainerSpacing: number = spacing !== undefined ? spacing : isMobile ? 1 : 2;
    const audioKey: string = Array.isArray(src) ? src[0] : src;
    const currentTimeComp = (
        <Grid>
            <Typography className={classNames.progressTime} onClick={toggleCurrentTime}>
                {showCurrentTime
                    ? getFormattedTime(state.player.current)
                    : getFormattedTime(state.player.remaining, true)}
            </Typography>
        </Grid>
    );

    return visible ? (
        <Grid
            container={true}
            spacing={mainContainerSpacing}
            component={Paper}
            elevation={0}
            className={cx(classNames.root)}
            sx={{
                padding: 0,
                alignItems: 'center',
                background: 'transparent',
                boxShadow: 'none',
            }}
        >
            <audio ref={player} hidden={true} preload={preload} key={audioKey}>
                {Array.isArray(src) ? (
                    src.map((srcLink, index) => <source key={index} src={srcLink} />)
                ) : (
                    <source src={src} />
                )}
            </audio>
            {loop && (
                <Grid
                    sx={{
                        padding: 0,
                    }}
                >
                    <Repeat
                        fontSize="large"
                        onClick={handleLoop}
                        className={cx(classNames.loopIcon)}
                    />
                </Grid>
            )}
            <Grid
                sx={{
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    lineHeight: 0,
                }}
            >
                <AudioPlayControl
                    classNames={classNames}
                    icons={icons}
                    playerStatus={state.player.status}
                    playerColors={playerColors}
                    replayAudio={_replayAudio}
                    pauseAudio={_pauseAudio}
                    playAudio={_playAudio}
                />
            </Grid>
            {download && <AudioDownloadsControl src={src} playerColors={playerColors} />}
            {volume && (
                <AudioVolumeControl
                    muted={muted}
                    muteAudio={_muteAudio}
                    unmuteAudio={_unmuteAudio}
                    classNames={classNames}
                    changeAudioVolume={_changeAudioVolume}
                    volume={state.player.volume}
                    playerColors={playerColors}
                    icons={icons}
                />
            )}
            {displaySlider && (
                <Grid
                    container
                    sx={{
                        padding: 0,
                    }}
                >
                    {(!isSingleTime || isTimePositionStart) && currentTimeComp}
                    <Grid
                        sx={{
                            padding: 0,
                        }}
                    >
                        <Slider onChange={handleAudioSliderChange} value={state.player.progress} />
                    </Grid>
                    {!isSingleTime && (
                        <Grid>
                            <Typography className={classNames.progressTime}>
                                {getFormattedTime(state?.player?.duration)}
                            </Typography>
                        </Grid>
                    )}
                    {isSingleTime && !isTimePositionStart && currentTimeComp}
                </Grid>
            )}
            {displayCloseButton && (
                <AudioPlayerCloseButton
                    onClose={handleClose}
                    classNames={classNames}
                    playerColors={playerColors}
                    icons={icons}
                />
            )}
        </Grid>
    ) : null;
};

export default AudioPlayer;
